import { useEffect } from "react";
import { useI18n } from "./I18nContext";
import { loadCatalog, type Catalog } from "./catalogs";
import type { Locale } from "./translations";

const originalText = new WeakMap<Text, string>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();
const translatedAttributes = ["alt", "aria-label", "placeholder", "title", "content"] as const;
const normalize = (value: string) => value.replace(/\s+/g, " ").trim();

function translateTextNode(node: Text, locale: Locale, catalog: Catalog) {
  const parent = node.parentElement;
  if (!parent || parent.closest("script,style,noscript,textarea,[contenteditable='true']")) return;
  const remembered = originalText.get(node); const raw = remembered ?? node.data; const source = normalize(raw);
  if (!source || (locale !== "en" && !catalog[source]) || (locale === "en" && !remembered)) return;
  if (!remembered) originalText.set(node, raw);
  const translated = locale === "en" ? source : catalog[source] ?? source;
  const leading = raw.match(/^\s*/)?.[0] ?? ""; const trailing = raw.match(/\s*$/)?.[0] ?? ""; const next = `${leading}${translated}${trailing}`;
  if (node.data !== next) node.data = next;
}

function translateElement(element: Element, locale: Locale, catalog: Catalog) {
  for (const attribute of translatedAttributes) {
    const current = element.getAttribute(attribute); if (!current) continue;
    let originals = originalAttributes.get(element); const remembered = originals?.get(attribute); const source = remembered ?? normalize(current);
    if ((locale !== "en" && !catalog[source]) || (locale === "en" && !remembered)) continue;
    if (!originals) { originals = new Map(); originalAttributes.set(element, originals); }
    if (!remembered) originals.set(attribute, source);
    const next = locale === "en" ? source : catalog[source] ?? source;
    if (current !== next) element.setAttribute(attribute, next);
  }
}

function translateTree(root: Node, locale: Locale, catalog: Catalog) {
  if (root.nodeType === Node.TEXT_NODE) { translateTextNode(root as Text, locale, catalog); return; }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
  if (root instanceof Element) translateElement(root, locale, catalog);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT); let node: Node | null;
  while ((node = walker.nextNode())) node.nodeType === Node.TEXT_NODE ? translateTextNode(node as Text, locale, catalog) : translateElement(node as Element, locale, catalog);
}

export default function CatalogTranslationBridge() {
  const { locale } = useI18n();
  useEffect(() => {
    let cancelled = false; let observer: MutationObserver | undefined;
    void loadCatalog(locale).then(catalog => {
      if (cancelled) return;
      const root = document.documentElement; translateTree(root, locale, catalog);
      observer = new MutationObserver(records => { for (const record of records) {
        if (record.type === "attributes") translateElement(record.target as Element, locale, catalog);
        else if (record.type === "characterData") translateTextNode(record.target as Text, locale, catalog);
        else record.addedNodes.forEach(node => translateTree(node, locale, catalog));
      }});
      observer.observe(root, { subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:[...translatedAttributes] });
    });
    return () => { cancelled = true; observer?.disconnect(); };
  }, [locale]);
  return null;
}
